import React from "react";
import { getClassName } from "@utils";
import { Skeleton, SkeletonSize, SkeletonVariant } from "../Skeleton";
import { InfoLabel } from "../InfoLabel";
import { Tooltip, TooltipSize } from "../Tooltip";
import { ArrowDownIcon, ArrowUpIcon } from "./icons";
import css from "./XRayAnalysis.module.scss";

export interface XRayAnalysisItem {
  textAfter: string;
  textBefore: string;
  token: string;
  isPositive: boolean;
}

export interface XRayAnalysisProps {
  isLoading: boolean;
  items: XRayAnalysisItem[];
}

const XRayAnalysis: React.FC<XRayAnalysisProps> = ({ isLoading, items }) => {
  const attrs = {
    container: {
      className: getClassName(css.container, isLoading && css.containerLoading),
    },
    title: {
      className: css.title,
    },
    item: {
      className: css.item,
    },
    textSkeleton: {
      size: SkeletonSize.L,
    },
    iconSkeleton: {
      variant: SkeletonVariant.Circle,
      size: SkeletonSize.S,
      className: css.iconSkeleton,
    },
    infoLabel: {
      className: css.infoLabel,
      text: "We didn’t detect X-Ray signals in this message",
    },
  };

  const renderXRayItems = () =>
    items.map(({ textAfter, textBefore, token, isPositive }, index) => {
      const IconComponent = isPositive ? ArrowUpIcon : ArrowDownIcon;
      const attrs = {
        item: {
          className: css.item,
        },
        text: {
          className: css.text,
        },
        token: {
          className: css.token,
        },
        iconWrapper: {
          className: getClassName(css.iconWrapper, isPositive && css.iconWrapperPositive),
        },
        icon: {
          className: css.icon,
        },
        tooltip: {
          className: css.tooltip,
          size: TooltipSize.XS,
          text: isPositive
            ? "Nonverbal cues that convey authority. Sending out these signals can improve the sender’s chances of gaining the Upper-Hand."
            : "Nonverbal cues that convey less authority. Sending out these signals may decrease your chances of gaining the Upper-Hand.",
          arrow: false,
          autoPosition: true,
        },
      };

      return (
        <div key={index} {...attrs.item}>
          <div {...attrs.text}>
            <span>{textBefore}</span>
            <span {...attrs.token}>{token}</span>
            <span>{textAfter}</span>
          </div>
          <Tooltip {...attrs.tooltip}>
            <div {...attrs.iconWrapper}>
              <IconComponent {...attrs.icon} />
            </div>
          </Tooltip>
        </div>
      );
    });

  if (isLoading) {
    return (
      <div {...attrs.container}>
        <div {...attrs.title}>
          <span>Message pointer</span>
          <span>Authority Change</span>
        </div>
        <div {...attrs.item}>
          <Skeleton {...attrs.textSkeleton} />
          <Skeleton {...attrs.iconSkeleton} />
        </div>
        <div {...attrs.item}>
          <Skeleton {...attrs.textSkeleton} />
          <Skeleton {...attrs.iconSkeleton} />
        </div>
        <div {...attrs.item}>
          <Skeleton {...attrs.textSkeleton} />
          <Skeleton {...attrs.iconSkeleton} />
        </div>
        <div {...attrs.item}>
          <Skeleton {...attrs.textSkeleton} />
          <Skeleton {...attrs.iconSkeleton} />
        </div>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div {...attrs.container}>
        <div {...attrs.title}>
          <span>Message pointer</span>
          <span>Authority Change</span>
        </div>
        <div {...attrs.item}>
          <InfoLabel {...attrs.infoLabel} />
        </div>
      </div>
    );
  }

  return (
    <div {...attrs.container}>
      <div {...attrs.title}>
        <span>Message pointer</span>
        <span>Authority Change</span>
      </div>
      {renderXRayItems()}
    </div>
  );
};

export default XRayAnalysis;
